import React, { useMemo } from 'react';
import {
    Alert,
    Box,
    CircularProgress,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    Typography,
} from '@mui/material';
import {
    ActivitySnapshotStatus,
    useGetActivityChangesQuery,
} from '../../../../services';
import {
    buildRowLabels,
    buildValueLabels,
    describeChangePath,
    diffSnapshots,
    formatBusinessDateTime,
    formatChangePair,
    getActivityEntityLabel,
    getActivityTypeLabel,
    getSnapshotStatusMessage,
    isSnapshotCaptureFailed,
    Snapshot,
} from '../../../../helpers';

// The snapshots cross the wire as strings rather than a JSON scalar (see the
// feature doc §2a — a scalar dependency risked this repo's fragile install). A
// malformed payload should show an empty diff, never crash the panel, so
// parsing is defensive.
function parseSnapshot(raw: string | null | undefined): Snapshot {
    if (!raw) return null;
    try {
        return JSON.parse(raw) as Snapshot;
    } catch {
        return null;
    }
}

interface IActivityChangesContentProps {
    activityId: number | null;
    /** Skip fetching until the panel is actually visible. */
    active?: boolean;
    /**
     * The history panel already shows type / user / date in its row summary, so
     * it turns the header off to avoid repeating itself.
     */
    showHeader?: boolean;
}

/**
 * Renders one activity's before/after diff. Shared by the standalone dialog
 * (activities page) and the Historial tab on the sale form, so the two can
 * never drift.
 */
export function ActivityChangesContent({
    activityId,
    active = true,
    showHeader = true,
}: IActivityChangesContentProps) {
    const { data, loading } = useGetActivityChangesQuery({
        variables: { ActivityId: activityId! },
        skip: !activityId || !active,
        fetchPolicy: 'cache-and-network',
    });

    const activity = data?.getActivity;

    const { changes, rowLabels, valueLabels } = useMemo(() => {
        const oldData = parseSnapshot(activity?.old_data);
        const newData = parseSnapshot(activity?.new_data);
        return {
            changes: diffSnapshots(oldData, newData),
            rowLabels: buildRowLabels(oldData, newData),
            // Foreign keys carry the related row's name beside them, so a status
            // change reads "Entregado → Cotización" and not "2 → 1".
            valueLabels: buildValueLabels(oldData, newData),
        };
    }, [activity?.old_data, activity?.new_data]);

    // Why the snapshots are missing, when they are. Three different situations
    // used to collapse into one empty table that read like a bug; the server now
    // says which, and this panel renders that answer rather than guessing from
    // the nullable JSON.
    //
    // `legacy` is the one status that still consults the JSON: those rows were
    // written before the status existed and some of them do carry snapshots, so
    // a real diff wins over the legacy explanation.
    const status =
        activity?.snapshot_status ?? ActivitySnapshotStatus.Legacy;
    const hasSnapshots =
        !!activity && (!!activity.old_data || !!activity.new_data);
    const statusMessage =
        status === ActivitySnapshotStatus.Legacy && hasSnapshots
            ? null
            : getSnapshotStatusMessage(status);

    if (loading && !activity) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                <CircularProgress />
            </Box>
        );
    }

    if (!activity) {
        return <Typography>No se encontró la actividad.</Typography>;
    }

    return (
        <Box>
            {showHeader ? (
                <Box sx={{ mb: 2 }}>
                    <Typography variant={'subtitle1'}>
                        {getActivityTypeLabel(activity.type)} ·{' '}
                        {getActivityEntityLabel(activity.entity_name)}
                    </Typography>
                    <Typography variant={'body2'} color={'text.secondary'}>
                        {activity.title}
                        {activity.user?.fullname
                            ? ` · ${activity.user.fullname}`
                            : ''}
                        {/*
                          Mexico City business day, like every other activity
                          timestamp — not the viewer's machine clock.
                        */}
                        {activity.created_at
                            ? ` · ${formatBusinessDateTime(activity.created_at)}`
                            : ''}
                    </Typography>
                </Box>
            ) : null}

            {statusMessage ? (
                // Amber only for a real failure. `legacy` and `not_supported`
                // are ordinary facts about the row, and dressing them as
                // warnings would teach the reader to ignore the one that matters.
                isSnapshotCaptureFailed(status) ? (
                    <Alert severity={'warning'} variant={'outlined'}>
                        {statusMessage}
                    </Alert>
                ) : (
                    <Typography color={'text.secondary'} variant={'body2'}>
                        {statusMessage}
                    </Typography>
                )
            ) : changes.length === 0 ? (
                <Typography color={'text.secondary'} variant={'body2'}>
                    No cambió ningún campo.
                </Typography>
            ) : (
                <Table size={'small'}>
                    <TableHead>
                        <TableRow>
                            <TableCell
                                align={'left'}
                                sx={{ textAlign: 'left' }}
                            >
                                Campo
                            </TableCell>
                            <TableCell
                                align={'left'}
                                sx={{ textAlign: 'left' }}
                            >
                                Antes
                            </TableCell>
                            <TableCell
                                align={'left'}
                                sx={{ textAlign: 'left' }}
                            >
                                Después
                            </TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {changes.map((change) => {
                            const pair = formatChangePair(
                                change.old,
                                change.new,
                                valueLabels[change.path],
                                change.path,
                            );
                            // Numbers right, text left — house rule. A labelled
                            // foreign key is text, even though its raw value is
                            // a number.
                            // Align each rendered cell by its own value. A
                            // missing side is a dash and stays left-aligned,
                            // even when the other side is numeric.
                            const oldAlign = pair.oldIsNumeric
                                ? 'right'
                                : 'left';
                            const newAlign = pair.newIsNumeric
                                ? 'right'
                                : 'left';
                            return (
                                <TableRow key={change.path}>
                                    <TableCell
                                        align={'left'}
                                        sx={{ textAlign: 'left' }}
                                    >
                                        {describeChangePath(
                                            change.path,
                                            rowLabels,
                                            activity.entity_name,
                                        )}
                                    </TableCell>
                                    <TableCell
                                        align={oldAlign}
                                        title={pair.oldTitle}
                                        sx={{
                                            color: 'text.secondary',
                                            textAlign: oldAlign,
                                        }}
                                    >
                                        {pair.old}
                                    </TableCell>
                                    <TableCell
                                        align={newAlign}
                                        title={pair.newTitle}
                                        sx={{
                                            fontWeight: 600,
                                            textAlign: newAlign,
                                        }}
                                    >
                                        {pair.new}
                                    </TableCell>
                                </TableRow>
                            );
                        })}
                    </TableBody>
                </Table>
            )}
        </Box>
    );
}
